import { Text } from '@shared/components';
import { useToast } from '@shared/components/toast';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import {
  MeDocument,
  useUpdateUserSettingsMutation,
} from '@shared/generated/graphql';
import { AutoReplySettings } from '../AutoReplySettings';
import { AutoReplySettingsForm } from '../types';
import { AUTO_REPLY_HELPER_TEXT, HIERARCHY_WARNING } from '../utils';

type UserAutoReplySettingsProps = {
  quoteIds: string[];
  originatorName?: string;
  handleClose: () => void;
  handleChangeLevel: () => void;
};

export const UserAutoReplySettings = ({
  quoteIds,
  originatorName,
  handleClose,
  handleChangeLevel,
}: UserAutoReplySettingsProps) => {
  const { me } = useMeContext();
  const [updateUserSettings, { loading }] = useUpdateUserSettingsMutation();
  const { sendToast } = useToast();

  const handleSubmit = async (
    autoReply: AutoReplySettingsForm,
    shouldClose?: boolean
  ) => {
    if (!me?.id) {
      sendToast('Unexpected error updating settings', {
        variant: 'error',
      });
      return;
    }

    const { __typename, ...otherSettings } = me.settings || {};
    const settings = {
      ...otherSettings,
      autoReply,
    };

    try {
      const res = await updateUserSettings({
        variables: {
          input: {
            id: me?.id,
            settings,
          },
        },
        refetchQueries: [MeDocument],
      });

      if (res.data?.updateUserSettings.success) {
        sendToast('Settings updated', {
          variant: 'success',
        });
        shouldClose && handleClose();
        return;
      }
    } catch (err) {
      console.error(err);
    }

    sendToast('Error updating settings', {
      variant: 'error',
    });
  };

  return (
    <AutoReplySettings
      quoteIds={quoteIds}
      onSubmit={handleSubmit}
      loadingSubmit={loading}
      autoReplySettings={me?.settings?.autoReply}
      handleChangeLevel={handleChangeLevel}
      editLevelDesc={`Edit instructions for ${originatorName} instead`}
      header={
        <div className="flex flex-col gap-2">
          <Text type="body-sm" className="text-gray-700">
            {AUTO_REPLY_HELPER_TEXT}
          </Text>
          <Text type="body-sm" isHeavy={true} className="text-gray-700">
            {HIERARCHY_WARNING}
          </Text>
        </div>
      }
    />
  );
};
